import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, X, Star } from 'lucide-react';

const empty = { text: '', author: '', event: '', rating: 5, order: 0 };

const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/testimonials');
      setItems(data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing({}); };
  const openEdit = (it) => { setForm({ ...it }); setEditing(it); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, rating: Number(form.rating) || 5, order: Number(form.order) || 0 };
      if (editing && editing.id) {
        await api.patch(`/admin/testimonials/${editing.id}`, payload);
        toast.success('Témoignage mis à jour');
      } else {
        await api.post('/admin/testimonials', payload);
        toast.success('Témoignage ajouté');
      }
      close();
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success('Témoignage supprimé');
      load();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div data-testid="admin-testimonials-manager">
      <div className="flex flex-wrap justify-between items-center gap-6 mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-2 block">Voix de nos clients</span>
          <h2 className="text-4xl font-serif text-[#111111]">Témoignages</h2>
        </div>
        <button
          onClick={openNew}
          data-testid="testi-add-btn"
          className="w-full sm:w-auto justify-center flex items-center gap-3 px-6 py-3 border border-[#111111] text-xs uppercase tracking-[0.3em] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-all"
        >
          <Plus className="w-4 h-4" /> Ajouter un témoignage
        </button>
      </div>

      {loading ? (
        <p className="text-center text-[#6B6B6B] py-20">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#111111]/20">
          <p className="text-[#6B6B6B]">Aucun témoignage.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((it) => (
            <div key={it.id} className={`border border-[#111111]/10 p-8 bg-white relative group ${it.hidden ? 'opacity-50' : ''}`} data-testid={`testi-item-${it.id}`}>
              {it.hidden && <span className="block mb-4 text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B]" data-testid={`testi-hidden-badge-${it.id}`}>Masqué du site</span>}
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: it.rating || 5 }).map((_, i) => (
                  <Star key={i} className="w-3 h-3 fill-[#111111] text-[#111111]" />
                ))}
              </div>
              <p className="font-serif italic text-[#111111]/80 mb-5 leading-relaxed">"{it.text}"</p>
              <div className="border-t border-[#111111]/10 pt-4">
                <p className="text-xs uppercase tracking-[0.25em] text-[#111111] font-semibold">{it.author}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">{it.event}</p>
              </div>
              <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEdit(it)} data-testid={`testi-edit-${it.id}`} className="w-9 h-9 bg-white flex items-center justify-center border border-[#111111]/20 hover:border-[#111111]">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => del(it.id)} data-testid={`testi-delete-${it.id}`} className="w-9 h-9 bg-white flex items-center justify-center border border-[#111111]/20 hover:border-red-500 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-xl p-10 border-t-4 border-[#111111] relative"
            data-testid="testi-form"
          >
            <button type="button" onClick={close} className="absolute top-5 right-5 text-[#6B6B6B] hover:text-[#111111]">
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-2xl font-serif mb-8 text-[#111111]">
              {editing.id ? 'Modifier le témoignage' : 'Nouveau témoignage'}
            </h3>
            <div className="space-y-5">
              <Field label="Texte du témoignage" required>
                <textarea data-testid="testi-form-text" required rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="luxury-input-light" />
              </Field>
              <Field label="Auteur" required>
                <input data-testid="testi-form-author" required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="luxury-input-light" />
              </Field>
              <Field label="Événement">
                <input data-testid="testi-form-event" value={form.event || ''} onChange={(e) => setForm({ ...form, event: e.target.value })} placeholder="Mariage, Gala, etc." className="luxury-input-light" />
              </Field>
              <div className="grid grid-cols-2 gap-5">
                <Field label="Note (1-5)">
                  <input data-testid="testi-form-rating" type="number" min={1} max={5} value={form.rating || 5} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="luxury-input-light" />
                </Field>
                <Field label="Ordre">
                  <input data-testid="testi-form-order" type="number" value={form.order || 0} onChange={(e) => setForm({ ...form, order: e.target.value })} className="luxury-input-light" />
                </Field>
              </div>
              <label className="flex items-center gap-3 text-xs text-[#111111] cursor-pointer">
                <input type="checkbox" data-testid="testi-form-hidden" checked={!!form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} className="accent-[#111111]" />
                Masquer du site public (conservé dans l'admin)
              </label>
            </div>
            <div className="flex gap-4 mt-8">
              <button type="button" onClick={close} className="px-6 py-3 text-xs uppercase tracking-[0.3em] border border-[#111111]/30 hover:bg-[#F5F5F5]">Annuler</button>
              <button type="submit" data-testid="testi-form-submit" className="flex-1 px-6 py-3 text-xs uppercase tracking-[0.3em] bg-[#111111] text-white hover:bg-[#333333] transition-colors">
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-2 block">
      {label}{required && <span className="text-[#111111]"> *</span>}
    </span>
    {children}
  </label>
);

export default TestimonialsManager;
